import React, { useState, useMemo } from 'react';

// IRS Uniform Lifetime Table (Table III) - Effective January 1, 2022
// Source: IRS Publication 590-B, Appendix B
const UNIFORM_LIFETIME_TABLE = {
  72: 27.4, 73: 26.5, 74: 25.5, 75: 24.6, 76: 23.7, 77: 22.9, 78: 22.0, 79: 21.1,
  80: 20.2, 81: 19.4, 82: 18.5, 83: 17.7, 84: 16.8, 85: 16.0, 86: 15.2, 87: 14.4,
  88: 13.7, 89: 12.9, 90: 12.2, 91: 11.5, 92: 10.8, 93: 10.1, 94: 9.5, 95: 8.9,
  96: 8.4, 97: 7.8, 98: 7.3, 99: 6.8, 100: 6.4, 101: 6.0, 102: 5.6, 103: 5.2,
  104: 4.9, 105: 4.6, 106: 4.3, 107: 4.1, 108: 3.9, 109: 3.7, 110: 3.5, 111: 3.4,
  112: 3.3, 113: 3.1, 114: 3.0, 115: 2.9, 116: 2.8, 117: 2.7, 118: 2.5, 119: 2.3,
  120: 2.0
};

// IRS Single Life Expectancy Table (Table I) - Effective January 1, 2022
// Source: IRS Publication 590-B, Appendix B
const SINGLE_LIFE_TABLE = {
  0: 84.6, 1: 83.7, 2: 82.8, 3: 81.8, 4: 80.8, 5: 79.8, 6: 78.8, 7: 77.9,
  8: 76.9, 9: 75.9, 10: 74.9, 11: 73.9, 12: 72.9, 13: 71.9, 14: 70.9, 15: 69.9,
  16: 69.0, 17: 68.0, 18: 67.0, 19: 66.0, 20: 65.0, 21: 64.1, 22: 63.1, 23: 62.1,
  24: 61.1, 25: 60.2, 26: 59.2, 27: 58.2, 28: 57.3, 29: 56.3, 30: 55.3, 31: 54.4,
  32: 53.4, 33: 52.5, 34: 51.5, 35: 50.5, 36: 49.6, 37: 48.6, 38: 47.7, 39: 46.7,
  40: 45.7, 41: 44.8, 42: 43.8, 43: 42.9, 44: 41.9, 45: 41.0, 46: 40.0, 47: 39.0,
  48: 38.1, 49: 37.1, 50: 36.2, 51: 35.3, 52: 34.3, 53: 33.4, 54: 32.5, 55: 31.6,
  56: 30.6, 57: 29.8, 58: 28.9, 59: 28.0, 60: 27.1, 61: 26.2, 62: 25.4, 63: 24.5,
  64: 23.7, 65: 22.9, 66: 22.0, 67: 21.2, 68: 20.4, 69: 19.6, 70: 18.8, 71: 18.0,
  72: 17.2, 73: 16.4, 74: 15.6, 75: 14.8, 76: 14.1, 77: 13.3, 78: 12.6, 79: 11.9,
  80: 11.2, 81: 10.5, 82: 9.9, 83: 9.3, 84: 8.7, 85: 8.1, 86: 7.6, 87: 7.1,
  88: 6.6, 89: 6.1, 90: 5.7, 91: 5.3, 92: 4.9, 93: 4.6, 94: 4.3, 95: 4.0,
  96: 3.7, 97: 3.4, 98: 3.2, 99: 3.0, 100: 2.8, 101: 2.6, 102: 2.5, 103: 2.3,
  104: 2.2, 105: 2.1, 106: 2.1, 107: 2.1, 108: 2.0, 109: 2.0, 110: 2.0, 111: 2.0,
  112: 2.0, 113: 1.9, 114: 1.9, 115: 1.8, 116: 1.8, 117: 1.6, 118: 1.4, 119: 1.1,
  120: 1.0
};

// Get distribution period from Uniform Lifetime Table
const getDistributionPeriod = (age) => {
  if (age < 72) return null;
  if (age > 120) return UNIFORM_LIFETIME_TABLE[120];
  return UNIFORM_LIFETIME_TABLE[age];
};

// Get life expectancy from Single Life Table
const getSingleLifeExpectancy = (age) => {
  if (age < 0) return SINGLE_LIFE_TABLE[0];
  if (age > 120) return SINGLE_LIFE_TABLE[120];
  return SINGLE_LIFE_TABLE[Math.floor(age)];
};

// Determine RMD starting age based on birth year (SECURE 2.0)
const getRMDStartAge = (birthYear) => {
  if (birthYear <= 1950) return 72;
  if (birthYear >= 1951 && birthYear <= 1959) return 73;
  return 75; // Born 1960 or later
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);
};

export default function RMDCalculator() {
  const currentYear = new Date().getFullYear();
  
  // Standard RMD Inputs
  const [birthYear, setBirthYear] = useState(1960);
  const [accountBalance, setAccountBalance] = useState(500000);
  const [growthRate, setGrowthRate] = useState(5);
  const [projectToAge, setProjectToAge] = useState(95);
  
  // Inherited IRA Inputs
  const [inheritedBalance, setInheritedBalance] = useState(250000);
  const [beneficiaryAge, setBeneficiaryAge] = useState(50);
  const [inheritedGrowthRate, setInheritedGrowthRate] = useState(5);
  const [isEligibleDesignatedBeneficiary, setIsEligibleDesignatedBeneficiary] = useState(false);
  
  const [activeTab, setActiveTab] = useState('standard');
  const [showDetails, setShowDetails] = useState(false);

  // Select all on focus handler
  const handleFocus = (e) => e.target.select();

  // Calculate current age and RMD start age
  const currentAge = currentYear - birthYear;
  const rmdStartAge = getRMDStartAge(birthYear);

  // Calculate Standard RMD Projections - START FROM CURRENT AGE
  const standardRMDProjections = useMemo(() => {
    const projections = [];
    let balance = accountBalance;
    let age = currentAge;
    const endAge = Math.max(projectToAge, 100);
    
    while (age <= endAge && balance > 0) {
      // Only calculate RMD if at or past RMD start age
      const isRMDAge = age >= rmdStartAge;
      const distributionPeriod = isRMDAge ? getDistributionPeriod(age) : null;
      const rmd = distributionPeriod ? balance / distributionPeriod : 0;
      const balanceAfterRMD = balance - rmd;
      const growth = balanceAfterRMD * (growthRate / 100);
      const endingBalance = balanceAfterRMD + growth;
      
      projections.push({
        year: currentYear + (age - currentAge),
        age,
        beginningBalance: balance,
        distributionPeriod: distributionPeriod || '—',
        rmd: rmd,
        growth: growth,
        endingBalance: Math.max(0, endingBalance),
        isPreRMD: !isRMDAge
      });
      
      balance = Math.max(0, endingBalance);
      age++;
    }
    
    return projections;
  }, [currentAge, rmdStartAge, accountBalance, growthRate, projectToAge, currentYear]);

  // Calculate Inherited IRA Projections
  const inheritedIRAProjections = useMemo(() => {
    const projections = [];
    let balance = inheritedBalance;
    let benefAge = beneficiaryAge;
    
    if (isEligibleDesignatedBeneficiary) {
      // Eligible Designated Beneficiary: Use Single Life Table, reduce by 1 each year
      let lifeExpectancyFactor = getSingleLifeExpectancy(benefAge);
      let year = 0;
      
      while (balance > 100 && lifeExpectancyFactor > 0) {
        const rmd = balance / lifeExpectancyFactor;
        const balanceAfterRMD = balance - rmd;
        const growth = balanceAfterRMD * (inheritedGrowthRate / 100);
        const endingBalance = balanceAfterRMD + growth;
        
        projections.push({
          year: currentYear + year,
          beneficiaryAge: benefAge,
          beginningBalance: balance,
          lifeExpectancyFactor: lifeExpectancyFactor.toFixed(1),
          requiredDistribution: rmd,
          growth: growth,
          endingBalance: Math.max(0, endingBalance)
        });
        
        balance = Math.max(0, endingBalance);
        benefAge++;
        lifeExpectancyFactor -= 1;
        year++;
        
        if (year > 50) break;
      }
    } else {
      // Non-Eligible Designated Beneficiary: 10-Year Rule
      for (let year = 1; year <= 10; year++) {
        const yearsRemaining = 11 - year;
        const rmd = year < 10 ? balance / yearsRemaining : balance;
        const balanceAfterRMD = balance - rmd;
        const growth = year < 10 ? balanceAfterRMD * (inheritedGrowthRate / 100) : 0;
        const endingBalance = balanceAfterRMD + growth;
        
        projections.push({
          year: currentYear + year - 1,
          yearOfInheritance: year,
          beneficiaryAge: benefAge + year - 1,
          beginningBalance: balance,
          yearsRemaining,
          requiredDistribution: rmd,
          growth: growth,
          endingBalance: Math.max(0, endingBalance)
        });
        
        balance = Math.max(0, endingBalance);
      }
    }
    
    return projections;
  }, [inheritedBalance, beneficiaryAge, inheritedGrowthRate, isEligibleDesignatedBeneficiary, currentYear]);

  const totalStandardRMDs = standardRMDProjections.reduce((sum, p) => sum + p.rmd, 0);
  const totalInheritedDistributions = inheritedIRAProjections.reduce((sum, p) => sum + p.requiredDistribution, 0);
  
  // Find the first RMD year
  const firstRMDProjection = standardRMDProjections.find(p => p.rmd > 0);

  return (
    <div className="min-h-screen bg-stone-100 p-6 font-sans">
      <div className="max-w-7xl mx-auto">
        {/* Tab Navigation */}
        <div className="flex gap-8 mb-8 border-b border-stone-200">
          <button
            onClick={() => setActiveTab('standard')}
            className={`pb-3 text-sm font-medium tracking-wide transition-all ${
              activeTab === 'standard'
                ? 'text-stone-800 border-b-2 border-stone-800'
                : 'text-stone-400 hover:text-stone-600'
            }`}
          >
            Standard RMD
          </button>
          <button
            onClick={() => setActiveTab('inherited')}
            className={`pb-3 text-sm font-medium tracking-wide transition-all ${
              activeTab === 'inherited'
                ? 'text-stone-800 border-b-2 border-stone-800'
                : 'text-stone-400 hover:text-stone-600'
            }`}
          >
            Inherited IRA
          </button>
        </div>

        {/* ============ STANDARD RMD TAB ============ */}
        {activeTab === 'standard' && (
          <div className="space-y-6">
            {/* 4 Input Cards - Side by Side */}
            <div className="grid grid-cols-4 gap-4">
              {/* Card 1: TIMELINE */}
              <div className="bg-white rounded-lg p-5 shadow-sm">
                <p className="text-xs font-medium text-stone-400 tracking-wider uppercase mb-4">Timeline</p>
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs text-stone-500 mb-1">Birth Year</label>
                    <input
                      type="number"
                      value={birthYear}
                      onChange={(e) => setBirthYear(parseInt(e.target.value) || 1950)}
                      onFocus={handleFocus}
                      className="w-full bg-stone-50 text-stone-800 px-3 py-2 rounded border border-stone-200 focus:border-stone-400 focus:outline-none text-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-stone-500 mb-1">Project to Age</label>
                    <input
                      type="number"
                      value={projectToAge}
                      onChange={(e) => setProjectToAge(parseInt(e.target.value) || 95)}
                      onFocus={handleFocus}
                      className="w-full bg-stone-50 text-stone-800 px-3 py-2 rounded border border-stone-200 focus:border-stone-400 focus:outline-none text-lg"
                    />
                  </div>
                </div>
              </div>

              {/* Card 2: ACCOUNT BALANCE */}
              <div className="bg-white rounded-lg p-5 shadow-sm border-t-4 border-teal-600">
                <p className="text-xs font-medium text-teal-700 tracking-wider uppercase mb-4">Account Balance</p>
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs text-stone-500 mb-1">Current Balance</label>
                    <div className="flex items-center bg-stone-50 rounded border border-stone-200">
                      <span className="pl-3 text-stone-400">$</span>
                      <input
                        type="number"
                        value={accountBalance}
                        onChange={(e) => setAccountBalance(parseFloat(e.target.value) || 0)}
                        onFocus={handleFocus}
                        className="w-full bg-transparent text-stone-800 px-2 py-2 focus:outline-none text-lg"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-stone-500 mb-1">Growth Rate</label>
                    <div className="flex items-center bg-stone-50 rounded border border-stone-200">
                      <input
                        type="number"
                        step="0.1"
                        value={growthRate}
                        onChange={(e) => setGrowthRate(parseFloat(e.target.value) || 0)}
                        onFocus={handleFocus}
                        className="w-full bg-transparent text-stone-800 px-3 py-2 focus:outline-none text-lg"
                      />
                      <span className="pr-3 text-stone-400">%</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Card 3: IRS FACTOR */}
              <div className="bg-white rounded-lg p-5 shadow-sm border-t-4 border-amber-600">
                <p className="text-xs font-medium text-amber-700 tracking-wider uppercase mb-4">IRS Table III</p>
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs text-stone-500 mb-1">Current Age</label>
                    <div className="bg-stone-50 text-stone-800 px-3 py-2 rounded border border-stone-200 text-lg">
                      {currentAge}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-stone-500 mb-1">RMDs Begin Age</label>
                    <div className="bg-stone-50 text-stone-800 px-3 py-2 rounded border border-stone-200 text-lg">
                      {rmdStartAge}
                    </div>
                  </div>
                </div>
              </div>

              {/* Card 4: RMD DISTRIBUTION */}
              <div className="bg-white rounded-lg p-5 shadow-sm border-t-4 border-rose-700">
                <p className="text-xs font-medium text-rose-800 tracking-wider uppercase mb-4">RMD Distribution</p>
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs text-stone-500 mb-1">First RMD (Age {rmdStartAge})</label>
                    <div className="bg-stone-50 text-stone-800 px-3 py-2 rounded border border-stone-200 text-lg font-medium">
                      {firstRMDProjection ? formatCurrency(firstRMDProjection.rmd) : '$0'}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-stone-500 mb-1">Lifetime Total</label>
                    <div className="bg-stone-50 text-stone-800 px-3 py-2 rounded border border-stone-200 text-lg">
                      {formatCurrency(totalStandardRMDs)}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Show Details Button */}
            <div className="flex justify-end">
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="px-4 py-2 text-sm text-stone-600 bg-white rounded-lg border border-stone-200 hover:bg-stone-50 transition-colors shadow-sm"
              >
                {showDetails ? 'Hide Details' : 'Show Details'}
              </button>
            </div>

            {/* Summary Cards Row */}
            {showDetails && (
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-white rounded-lg p-5 shadow-sm border-l-4 border-teal-600">
                  <p className="text-xs font-medium text-stone-400 tracking-wider uppercase mb-1">
                    {currentAge >= rmdStartAge ? 'Current Year RMD' : `Balance at Age ${rmdStartAge}`}
                  </p>
                  <p className="text-2xl font-semibold text-teal-700">
                    {currentAge >= rmdStartAge 
                      ? (standardRMDProjections[0] ? formatCurrency(standardRMDProjections[0].rmd) : '$0')
                      : (firstRMDProjection ? formatCurrency(firstRMDProjection.beginningBalance) : '$0')}
                  </p>
                </div>
                <div className="bg-white rounded-lg p-5 shadow-sm border-l-4 border-amber-500">
                  <p className="text-xs font-medium text-stone-400 tracking-wider uppercase mb-1">
                    {currentAge >= rmdStartAge ? 'First RMD Factor' : `Years Until RMDs`}
                  </p>
                  <p className="text-2xl font-semibold text-amber-600">
                    {currentAge >= rmdStartAge 
                      ? getDistributionPeriod(currentAge)
                      : (rmdStartAge - currentAge)}
                  </p>
                </div>
                <div className="bg-white rounded-lg p-5 shadow-sm border-l-4 border-rose-600">
                  <p className="text-xs font-medium text-stone-400 tracking-wider uppercase mb-1">First RMD Amount</p>
                  <p className="text-2xl font-semibold text-rose-700">
                    {firstRMDProjection ? formatCurrency(firstRMDProjection.rmd) : '$0'}
                  </p>
                </div>
              </div>
            )}

            {/* RMD Schedule Table */}
            {showDetails && (
              <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                <div className="border-l-4 border-teal-600">
                  <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                    <table className="w-full">
                      <thead className="sticky top-0 bg-white border-b border-stone-200">
                        <tr className="text-stone-400">
                          <th className="text-center py-4 px-4 font-medium tracking-wider text-xs uppercase">Age</th>
                          <th className="text-right py-4 px-4 font-medium tracking-wider text-xs uppercase">Beginning</th>
                          <th className="text-center py-4 px-4 font-medium tracking-wider text-xs uppercase">Factor</th>
                          <th className="text-right py-4 px-4 font-medium tracking-wider text-xs uppercase">RMD</th>
                          <th className="text-right py-4 px-4 font-medium tracking-wider text-xs uppercase">Growth</th>
                          <th className="text-right py-4 px-4 font-medium tracking-wider text-xs uppercase">Ending</th>
                        </tr>
                      </thead>
                      <tbody className="text-base">
                        {standardRMDProjections.map((row, idx) => (
                          <tr 
                            key={idx} 
                            className={`border-b border-stone-100 hover:bg-stone-50 transition-colors ${row.isPreRMD ? 'bg-stone-50/50' : ''}`}
                          >
                            <td className="py-3 px-4 text-center font-medium text-stone-700">
                              {row.age}
                              {row.age === rmdStartAge && <span className="ml-1 text-xs text-teal-600">★</span>}
                            </td>
                            <td className="py-3 px-4 text-right text-stone-600">{formatCurrency(row.beginningBalance)}</td>
                            <td className="py-3 px-4 text-center text-stone-400">{row.distributionPeriod}</td>
                            <td className="py-3 px-4 text-right font-semibold text-teal-700">
                              {row.rmd > 0 ? formatCurrency(row.rmd) : '—'}
                            </td>
                            <td className="py-3 px-4 text-right text-stone-500">{formatCurrency(row.growth)}</td>
                            <td className="py-3 px-4 text-right text-stone-600">{formatCurrency(row.endingBalance)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Info Note */}
            {showDetails && (
              <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                <p className="text-xs text-amber-800">
                  <strong>IRS Table III (Uniform Lifetime Table):</strong> RMDs begin at age 73 for those born 1951-1959, and age 75 for those born 1960 or later (SECURE 2.0 Act). 
                  RMD = Prior Year-End Balance ÷ Distribution Period. ★ marks the first RMD year.
                </p>
              </div>
            )}
          </div>
        )}

        {/* ============ INHERITED IRA TAB ============ */}
        {activeTab === 'inherited' && (
          <div className="space-y-6">
            {/* 4 Input Cards - Side by Side */}
            <div className="grid grid-cols-4 gap-4">
              {/* Card 1: BENEFICIARY */}
              <div className="bg-white rounded-lg p-5 shadow-sm">
                <p className="text-xs font-medium text-stone-400 tracking-wider uppercase mb-4">Beneficiary</p>
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs text-stone-500 mb-1">Current Age</label>
                    <input
                      type="number"
                      value={beneficiaryAge}
                      onChange={(e) => setBeneficiaryAge(parseInt(e.target.value) || 0)}
                      onFocus={handleFocus}
                      className="w-full bg-stone-50 text-stone-800 px-3 py-2 rounded border border-stone-200 focus:border-stone-400 focus:outline-none text-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-stone-500 mb-1">Type</label>
                    <div className="flex rounded overflow-hidden border border-stone-200">
                      <button
                        onClick={() => setIsEligibleDesignatedBeneficiary(false)}
                        className={`flex-1 py-2 text-xs font-medium transition-colors ${
                          !isEligibleDesignatedBeneficiary
                            ? 'bg-stone-800 text-white'
                            : 'bg-stone-50 text-stone-600 hover:bg-stone-100'
                        }`}
                      >
                        10-Year
                      </button>
                      <button
                        onClick={() => setIsEligibleDesignatedBeneficiary(true)}
                        className={`flex-1 py-2 text-xs font-medium transition-colors ${
                          isEligibleDesignatedBeneficiary
                            ? 'bg-stone-800 text-white'
                            : 'bg-stone-50 text-stone-600 hover:bg-stone-100'
                        }`}
                      >
                        EDB
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Card 2: INHERITED BALANCE */}
              <div className="bg-white rounded-lg p-5 shadow-sm border-t-4 border-teal-600">
                <p className="text-xs font-medium text-teal-700 tracking-wider uppercase mb-4">Inherited Balance</p>
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs text-stone-500 mb-1">Asset Value</label>
                    <div className="flex items-center bg-stone-50 rounded border border-stone-200">
                      <span className="pl-3 text-stone-400">$</span>
                      <input
                        type="number"
                        value={inheritedBalance}
                        onChange={(e) => setInheritedBalance(parseFloat(e.target.value) || 0)}
                        onFocus={handleFocus}
                        className="w-full bg-transparent text-stone-800 px-2 py-2 focus:outline-none text-lg"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-stone-500 mb-1">Growth Rate</label>
                    <div className="flex items-center bg-stone-50 rounded border border-stone-200">
                      <input
                        type="number"
                        step="0.1"
                        value={inheritedGrowthRate}
                        onChange={(e) => setInheritedGrowthRate(parseFloat(e.target.value) || 0)}
                        onFocus={handleFocus}
                        className="w-full bg-transparent text-stone-800 px-3 py-2 focus:outline-none text-lg"
                      />
                      <span className="pr-3 text-stone-400">%</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Card 3: DISTRIBUTION METHOD */}
              <div className="bg-white rounded-lg p-5 shadow-sm border-t-4 border-amber-600">
                <p className="text-xs font-medium text-amber-700 tracking-wider uppercase mb-4">IRS Table I</p>
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs text-stone-500 mb-1">Rule Applied</label>
                    <div className="bg-stone-50 text-stone-800 px-3 py-2 rounded border border-stone-200 text-lg">
                      {isEligibleDesignatedBeneficiary ? 'Stretch' : '10-Year'}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-stone-500 mb-1">
                      {isEligibleDesignatedBeneficiary ? 'Life Expectancy' : 'Years Remaining'}
                    </label>
                    <div className="bg-stone-50 text-stone-800 px-3 py-2 rounded border border-stone-200 text-lg">
                      {inheritedIRAProjections[0] 
                        ? (isEligibleDesignatedBeneficiary 
                            ? inheritedIRAProjections[0].lifeExpectancyFactor 
                            : inheritedIRAProjections[0].yearsRemaining)
                        : '—'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Card 4: DISTRIBUTION AMOUNT */}
              <div className="bg-white rounded-lg p-5 shadow-sm border-t-4 border-rose-700">
                <p className="text-xs font-medium text-rose-800 tracking-wider uppercase mb-4">Distribution</p>
                <div className="space-y-4">
                  <div>
                    <label className="block text-xs text-stone-500 mb-1">Year 1 Amount</label>
                    <div className="bg-stone-50 text-stone-800 px-3 py-2 rounded border border-stone-200 text-lg font-medium">
                      {inheritedIRAProjections[0] ? formatCurrency(inheritedIRAProjections[0].requiredDistribution) : '$0'}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs text-stone-500 mb-1">Total Distributions</label>
                    <div className="bg-stone-50 text-stone-800 px-3 py-2 rounded border border-stone-200 text-lg">
                      {formatCurrency(totalInheritedDistributions)}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Show Details Button */}
            <div className="flex justify-end">
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="px-4 py-2 text-sm text-stone-600 bg-white rounded-lg border border-stone-200 hover:bg-stone-50 transition-colors shadow-sm"
              >
                {showDetails ? 'Hide Details' : 'Show Details'}
              </button>
            </div>

            {/* Summary Cards Row */}
            {showDetails && (
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-white rounded-lg p-5 shadow-sm border-l-4 border-teal-600">
                  <p className="text-xs font-medium text-stone-400 tracking-wider uppercase mb-1">Year 1 Distribution</p>
                  <p className="text-2xl font-semibold text-teal-700">
                    {inheritedIRAProjections[0] ? formatCurrency(inheritedIRAProjections[0].requiredDistribution) : '$0'}
                  </p>
                </div>
                <div className="bg-white rounded-lg p-5 shadow-sm border-l-4 border-amber-500">
                  <p className="text-xs font-medium text-stone-400 tracking-wider uppercase mb-1">Year 1 Growth</p>
                  <p className="text-2xl font-semibold text-amber-600">
                    {inheritedIRAProjections[0] ? formatCurrency(inheritedIRAProjections[0].growth) : '$0'}
                  </p>
                </div>
                <div className="bg-white rounded-lg p-5 shadow-sm border-l-4 border-rose-600">
                  <p className="text-xs font-medium text-stone-400 tracking-wider uppercase mb-1">
                    {isEligibleDesignatedBeneficiary ? 'Final Distribution' : 'Year 10 Final'}
                  </p>
                  <p className="text-2xl font-semibold text-rose-700">
                    {inheritedIRAProjections.length > 0 
                      ? formatCurrency(inheritedIRAProjections[inheritedIRAProjections.length - 1].requiredDistribution)
                      : '$0'}
                  </p>
                </div>
              </div>
            )}

            {/* Distribution Schedule Table */}
            {showDetails && (
              <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                <div className="border-l-4 border-teal-600">
                  <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                    <table className="w-full">
                      <thead className="sticky top-0 bg-white border-b border-stone-200">
                        <tr className="text-stone-400">
                          <th className="text-center py-4 px-4 font-medium tracking-wider text-xs uppercase">Age</th>
                          {!isEligibleDesignatedBeneficiary && (
                            <th className="text-center py-4 px-4 font-medium tracking-wider text-xs uppercase">Year</th>
                          )}
                          <th className="text-right py-4 px-4 font-medium tracking-wider text-xs uppercase">Beginning</th>
                          <th className="text-center py-4 px-4 font-medium tracking-wider text-xs uppercase">
                            {isEligibleDesignatedBeneficiary ? 'Factor' : 'Remaining'}
                          </th>
                          <th className="text-right py-4 px-4 font-medium tracking-wider text-xs uppercase">Distribution</th>
                          <th className="text-right py-4 px-4 font-medium tracking-wider text-xs uppercase">Growth</th>
                          <th className="text-right py-4 px-4 font-medium tracking-wider text-xs uppercase">Ending</th>
                        </tr>
                      </thead>
                      <tbody className="text-base">
                        {inheritedIRAProjections.map((row, idx) => (
                          <tr key={idx} className="border-b border-stone-100 hover:bg-stone-50 transition-colors">
                            <td className="py-3 px-4 text-center font-medium text-stone-700">{row.beneficiaryAge}</td>
                            {!isEligibleDesignatedBeneficiary && (
                              <td className="py-3 px-4 text-center text-stone-500">{row.yearOfInheritance}</td>
                            )}
                            <td className="py-3 px-4 text-right text-stone-600">{formatCurrency(row.beginningBalance)}</td>
                            <td className="py-3 px-4 text-center text-stone-400">
                              {isEligibleDesignatedBeneficiary ? row.lifeExpectancyFactor : row.yearsRemaining}
                            </td>
                            <td className="py-3 px-4 text-right font-semibold text-teal-700">
                              {formatCurrency(row.requiredDistribution)}
                            </td>
                            <td className="py-3 px-4 text-right text-stone-500">{formatCurrency(row.growth)}</td>
                            <td className="py-3 px-4 text-right text-stone-600">{formatCurrency(row.endingBalance)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Info Notes */}
            {showDetails && (
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-amber-50 rounded-lg p-4 border border-amber-200">
                  <p className="text-xs text-amber-800">
                    <strong>10-Year Rule (SECURE Act):</strong> Most non-spouse beneficiaries must fully distribute inherited IRA by end of year 10. 
                    Annual RMDs required in years 1-9 if original owner died on/after their RBD.
                  </p>
                </div>
                <div className="bg-teal-50 rounded-lg p-4 border border-teal-200">
                  <p className="text-xs text-teal-800">
                    <strong>EDB (Eligible Designated Beneficiary):</strong> Surviving spouse, minor child, disabled/chronically ill individual, 
                    or person not more than 10 years younger than decedent. Uses IRS Table I (Single Life), reduced by 1 each year.
                  </p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}